/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Debug Service - Browser Console Debug Functions
 * 
 * Provides comprehensive debugging capabilities through the browser console
 * when dev mode is enabled. All functions are accessible via window.debug.
 */

import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { competitorAI } from './tycoon/competitorAIService';
import { ProductManager } from './productManager';
import { marketShareService } from './tycoon/marketShareService';
import { researchTree } from '../data/researchTree';
import { getDebugHelpText } from '../constants/debugCommands';
import { GAME_CONFIG } from '../constants/gameConfig';
import { GAME_CONSTANTS } from '../config/gameConstants';
import { GAME_FORMULAS } from '../config/gameFormulas';
import { calculateResearcherRP } from '../utils/researchCalculations';
import { useBuildingStore } from '../stores/buildingStore';
import { useInventoryStore } from '../stores/inventoryStore';
import { useProductStore } from '../stores/productStore';
import { useCompanyStore } from '../stores/companyStore';
import { buildingService } from './tycoon/buildingService';
import { SalesCalculationService } from './tycoon/salesCalculationService';
import { MarketSimulatorService } from './tycoon/marketSimulatorService';
import { componentReleaseService } from './tycoon/componentReleaseService';
import { inventoryManagementService } from './tycoon/inventoryManagementService';
import type { CompanyState } from '../hooks/useTycoonSimulation';
import type { Product } from '@smartphone-tycoon/shared-core';
import type { Product as ProductType } from '@smartphone-tycoon/shared-core';
import type { ResearchState } from '@smartphone-tycoon/shared-core';

interface DebugAPI {
  time: {
    setSpeed: (multiplier: number) => void;
    pause: () => void;
    resume: () => void;
    jumpTo: (day: number, month: number, year: number) => void;
    advanceDays: (days: number) => void;
    getDate: () => { day: number; month: number; year: number };
  };
  company: {
    setCash: (amount: number) => void;
    addCash: (amount: number) => void;
    setReputation: (value: number) => void;
    setMarketShare: (value: number) => void;
    setResearchPoints: (amount: number) => void;
    resetBankruptcy: () => void;
    maxAll: () => void;
    getStats: () => any;
  };
  buildings: {
    list: () => Promise<void>;
    available: () => Promise<void>;
    buy: (buildingId: string) => void;
    capacity: () => Promise<void>;
    unlock: (buildingId: string) => void;
    unlockAll: () => void;
    complete: () => void;
  };
  products: {
    list: () => Promise<void>;
    skipPhase: (productIdOrName: string) => void;
    instantRelease: (productIdOrName: string) => void;
    setSales: (productIdOrName: string, units: number) => void;
    discontinue: (productIdOrName: string) => void;
    details: (productIdOrName: string) => Promise<void>;
  };
  sales: {
    summary: () => Promise<void>;
    details: (productIdOrName: string, format?: 'json' | 'csv' | 'yaml') => Promise<void>;
    breakdown: (productIdOrName: string, format?: 'json' | 'csv' | 'yaml') => Promise<void>;
    setSales: (productIdOrName: string, units: number) => void;
    history: () => Promise<void>;
    forecast: (productIdOrName: string) => Promise<void>;
  };
  inventory: {
    components: () => Promise<void>;
    set: (componentId: string, qty: number) => void;
    add: (componentId: string, qty: number) => void;
    order: (componentId: string, qty: number) => void;
    fillAll: () => void;
    fillAllTo: (amount: number) => void;
    devices: {
      list: () => Promise<void>;
      add: (productId: string, qty: number) => void;
      set: (productId: string, qty: number) => void;
    };
  };
  research: {
    unlockAll: () => void;
    unlock: (nodeId: string) => void;
    addPoints: (amount: number) => void;
    showTree: () => void;
    listUnlocked: () => void;
    listActive: () => void;
  };
  employees: {
    list: () => void;
    hire: (role: string, count?: number) => void;
    fire: (role: string, count?: number) => void;
    setRole: (role: string, count: number) => void;
    payroll: () => void;
    stats: () => void;
    hireAll: () => void;
    fireAll: () => void;
    add: (role: string, count?: number) => void;
    addAll: () => void;
  };
  competitors: {
    list: () => Promise<void>;
    inspect: (companyId: string) => Promise<void>;
    products: (companyId: string) => Promise<void>;
    metrics: () => Promise<void>;
    simulateLaunch: (companyId: string) => Promise<void>;
    internals: (companyId: string) => Promise<void>;
    inspectAll: () => Promise<void>;
    getStats: (companyId: string) => Promise<any>;
    nextReleases: () => void;
  };
  state: {
    export: () => any;
    import: (state: any) => void;
    inspect: () => void;
    history: () => void;
    clear: () => void;
  };
  settings: {
    enableAutoProgression: () => void;
    disableAutoProgression: () => void;
    enableWarnings: () => void;
    disableWarnings: () => void;
    prefillNames: () => void;
    clearNames: () => void;
  };
  help: () => void;
}

interface ProductActions {
  products: Product[];
  progressProductToPhase: (productId: string, phase: string, date?: any) => void;
  releaseProduct: (productId: string) => void;
  discontinueProduct: (productId: string) => void;
  updateProduct: (productId: string, updates: any) => void;
}

interface SimulationActions {
  changeSpeed: (speed: { label: string; multiplier: number }) => void;
  togglePause: () => void;
  setPaused: (paused: boolean) => void;
  availableSpeeds?: Array<{ multiplier: number; label?: string; name?: string }>;
}

interface EmployeeActions {
  hire: (role: string, count: number) => void;
  fire: (role: string, count: number) => void;
  setEmployees: (employees: any[]) => void;
  setHiringProcesses: (processes: any[]) => void;
}

class DebugService {
  private gameStateRef: { current: CompanyState | null } = { current: null };
  private updateStateRef: { current: ((updates: Partial<CompanyState>) => void) | null } = { current: null };
  private productActionsRef: { current: ProductActions | null } = { current: null };
  private simulationActionsRef: { current: SimulationActions | null } = { current: null };
  private employeeActionsRef: { current: EmployeeActions | null } = { current: null };
  private eventHistory: Array<Record<string, unknown>> = [];
  private enabled = false;

  private resolveSpeedByPosition(position: number) {
    const availableSpeeds = this.simulationActionsRef.current?.availableSpeeds
      ?.filter((speed) => speed.multiplier > 0);

    if (!availableSpeeds?.length) {
      return null;
    }

    if (!Number.isFinite(position) || position < 1 || position > availableSpeeds.length) {
      console.error(`Speed must be between 1 and ${availableSpeeds.length}`);
      return null;
    }

    const speed = availableSpeeds[Math.floor(position) - 1];
    return {
      position: Math.floor(position),
      label: speed.label ?? speed.name ?? `${speed.multiplier}x`,
      multiplier: speed.multiplier
    };
  }

  private resolveEmployeeRole(
    roleInput: string,
    roles: Record<string, { name?: string }>
  ) {
    if (!roleInput) {
      return null;
    }

    if (roles[roleInput]) {
      return roleInput;
    }

    const normalized = roleInput.trim().toLowerCase();
    const matchedRole = Object.keys(roles).find(
      (role) => role.toLowerCase() === normalized
    );

    return matchedRole || null;
  }

  private calculateDaysSince(
    startDate: { day: number; month: number; year: number },
    currentDate: { day: number; month: number; year: number }
  ): number {
    // Simple approximation: 30 days per month, 365 days per year
    const startDays = (startDate.year * 365) + (startDate.month * 30) + startDate.day;
    const currentDays = (currentDate.year * 365) + (currentDate.month * 30) + currentDate.day;
    return Math.max(0, currentDays - startDays);
  }

  initialize(refs: {
    gameState: CompanyState;
    updateState: (updates: Partial<CompanyState>) => void;
    productActions?: ProductActions;
    simulationActions?: SimulationActions;
    employeeActions?: EmployeeActions;
  }) {
    this.gameStateRef.current = refs.gameState;
    this.updateStateRef.current = refs.updateState;
    this.productActionsRef.current = refs.productActions || null;
    this.simulationActionsRef.current = refs.simulationActions || null;
    this.employeeActionsRef.current = refs.employeeActions || null;
    
    // Re-enable if already enabled to update references
    if (this.enabled && (window as any).debug) {
      // References updated, no need to recreate the API
      return;
    }
  }

  enable() {
    if (this.enabled) return;
    this.enabled = true;

    try {
      // Helper function to find product by ID or name (defined before debugAPI for scoping)
      const findProductByIdOrName = async (productIdOrName: string): Promise<Product | null> => {
        // Try to get product by ID first
        const productByIdResult = await ProductManager.getProduct(productIdOrName);
        if (productByIdResult.success && productByIdResult.data) {
          return productByIdResult.data;
        }

        // If not found by ID, search by name (case-insensitive, player products only)
        const allProductsResult = await ProductManager.getAllProducts();
        if (!allProductsResult.success || !allProductsResult.data) {
          return null;
        }

        const playerProducts = allProductsResult.data.filter(
          p => p.metadata.companyId === 'player'
        );

        const productByName = playerProducts.find(
          p => p.name.toLowerCase() === productIdOrName.toLowerCase()
        );

        return productByName || null;
      };

      // Create debug API
      const debugAPI: DebugAPI = {
      time: {
        setSpeed: (multiplier: number) => {
          const speedByPosition = this.resolveSpeedByPosition(multiplier);
          if (!speedByPosition || !this.simulationActionsRef.current?.changeSpeed) {
            return;
          }
          this.simulationActionsRef.current.changeSpeed({
            label: speedByPosition.label,
            multiplier: speedByPosition.multiplier
          });
          console.log(`⏱️ Game speed set to ${speedByPosition.position} (${speedByPosition.multiplier}x)`);
        },
        pause: () => {
          if (this.simulationActionsRef.current?.setPaused) {
            this.simulationActionsRef.current.setPaused(true);
            console.log('⏸️ Game paused');
          }
        },
        resume: () => {
          if (this.simulationActionsRef.current?.setPaused) {
            this.simulationActionsRef.current.setPaused(false);
            console.log('▶️ Game resumed');
          }
        },
        jumpTo: async (day: number, month: number, year: number) => {
          if (!this.updateStateRef.current || !this.gameStateRef.current) return;
          
          if (day < 1 || day > 30 || month < 1 || month > 12 || year < GAME_CONFIG.STARTING_YEAR) {
            console.error(`Invalid date. Day: 1-30 (got ${day}), Month: 1-12 (got ${month}), Year: >= ${GAME_CONFIG.STARTING_YEAR} (got ${year}). Example: debug.time.jumpTo(15, 6, 2010)`);
            return;
          }
          
          const currentState = this.gameStateRef.current;
          const currentTotalDays = (currentState.year - GAME_CONFIG.STARTING_YEAR) * 360 + (currentState.month - 1) * 30 + currentState.day;
          const targetTotalDays = (year - GAME_CONFIG.STARTING_YEAR) * 360 + (month - 1) * 30 + day;
          
          // If jumping forward in time, use advanceDays logic to ensure events are triggered
          if (targetTotalDays > currentTotalDays) {
            const daysToAdvance = targetTotalDays - currentTotalDays;
            console.log(`⏭️ Jumping forward ${daysToAdvance} days...`);
            // Call advanceDays directly through the debug API reference
            await ((window as any).debug?.time?.advanceDays?.(daysToAdvance) || 
              console.error('Debug time.advanceDays not available'));
          } else {
            // If jumping backward or to the same date, just update directly
            this.updateStateRef.current({ day, month, year });
            competitorAI.setCurrentGameDate({ day, month, year });
            console.log(`📅 Jumped to ${month}/${day}/${year}`);
          }
        },
        advanceDays: async (days: number) => {
          if (!this.gameStateRef.current || !this.updateStateRef.current || !this.simulationActionsRef.current) return;
          
          console.log(`⏭️ Advancing ${days} days...`);
          
          // Pause the game to control the advancement
          const wasRunning = !this.gameStateRef.current.isPaused;
          if (wasRunning) {
            this.simulationActionsRef.current.setPaused(true);
          }
          
          // Store the original date for component release checking
          const originalDate = {
            day: this.gameStateRef.current.day,
            month: this.gameStateRef.current.month,
            year: this.gameStateRef.current.year
          };
          
          // We need to manually simulate the game tick for each day
          // This ensures all game mechanics are properly processed
          for (let i = 0; i < days; i++) {
            // Get current state fresh each iteration
            const currentState = this.gameStateRef.current;
            if (!currentState) return;
            
            let { day, month, year } = currentState;
            
            // Store previous date for event checking
            const previousDate = { day, month, year };
            
            // Advance one day
            day++;
            if (day > 30) {
              day = 1;
              month++;
              if (month > 12) {
                month = 1;
                year++;
              }
            }
            
            // Create new date object for calculations
            const newDate = { day, month, year };
            
            // Update competitor AI with new date before state update
            competitorAI.setCurrentGameDate(newDate);
            
            // Important: We need to simulate the full game tick by updating all game state
            // This includes: cash flow, sales, research progress, hiring progress, product development
            
            // 1. Calculate daily sales and revenue (simulating simulateDailySales)
            let dailyRevenue = 0;
            let dailyCosts = 0;
            
            if (currentState.products && currentState.products.length > 0) {
              // Simulate sales for each released product
              currentState.products.forEach(product => {
                if (product.status === 'released') {
                  // Simple sales simulation - in reality this should use the sales calculation service
                  const estimatedDailySales = Math.floor(Math.random() * 1000 + 100); // Placeholder
                  dailyRevenue += estimatedDailySales * product.price;
                  dailyCosts += estimatedDailySales * product.productionCost;
                }
              });
              
              // Additional operational costs
              dailyCosts += Math.sqrt(currentState.products.length) * 500;
            }
            
            const dailyProfit = dailyRevenue - dailyCosts;
            
            // 2. Update research progress
            let newResearchPoints = currentState.researchPoints || 0;
            let updatedResearch = currentState.research;
            
            if (currentState.research) {
              // Calculate daily research points (matching useTycoonSimulation logic)
              const releasedProducts = (currentState.products || []).filter(p => p.status === 'released');
              
              // Investment-based RP generation
              const investmentRP = roundToThousandths(GAME_FORMULAS.research.getInvestmentRP(
                currentState.research.monthlyInvestment, 
                currentState.research.rpGenerationRate
              ));
              
              // Product-based RP generation (using reputation)
              const productRP = GAME_FORMULAS.research.getProductRP(
                currentState.reputation || 0, 
                releasedProducts.length
              );
              
              // Researcher-based RP generation
              const researchers = (currentState.employees || []).filter(emp => emp.role === 'researcher');
              const researcherRP = calculateResearcherRP(researchers, currentState.research.monthlyInvestment);
              
              // Total daily RP
              const dailyRP = roundToThousandths(investmentRP + productRP + researcherRP);
              newResearchPoints = roundToThousandths(newResearchPoints + dailyRP);
              
              // Progress active research
              if (currentState.research.activeResearch && currentState.research.activeResearch.length > 0) {
                const updatedActiveResearch = currentState.research.activeResearch.map(research => {
                  if (research.completed) return research;
                  
                  // Get research node details from research tree
                  const allNodes = researchTree.branches.flatMap(branch => branch.nodes);
                  const node = allNodes.find(n => n.id === research.nodeId);
                  if (!node) return research;
                  
                  // Calculate progress based on research speed bonuses
                  const speedBonus = currentState.research?.unlockedNodes?.includes('business_rp_advanced') ? 0.2 : 0;
                  const effectiveDuration = node.duration * (1 - speedBonus);
                  const progressPerDay = 100 / effectiveDuration;
                  const newProgress = Math.min(100, research.progress + progressPerDay);
                  
                  return {
                    ...research,
                    progress: newProgress,
                    completed: newProgress >= 100
                  };
                });
                
                // Move completed research to unlocked nodes
                const completedResearch = updatedActiveResearch.filter(r => r.completed && !currentState.research!.unlockedNodes.includes(r.nodeId));
                const newUnlockedNodes = [...(currentState.research.unlockedNodes || []), ...completedResearch.map(r => r.nodeId)];
                
                // Calculate new RP generation rate based on unlocks
                let newRPRate = 1;
                if (newUnlockedNodes.includes('business_rp_generation')) newRPRate += 0.2;
                if (newUnlockedNodes.includes('business_rp_advanced')) newRPRate += 0.35;
                if (newUnlockedNodes.includes('business_innovation_lab')) newRPRate += 0.5;
                
                updatedResearch = {
                  ...currentState.research,
                  activeResearch: updatedActiveResearch.filter(r => !r.completed),
                  unlockedNodes: newUnlockedNodes,
                  rpGenerationRate: newRPRate
                };
              }
            }
            
            // 3. Process hiring progress
            let updatedHiringProcesses = currentState.hiringProcesses || [];
            const completedHires: any[] = [];
            
            if (updatedHiringProcesses.length > 0) {
              updatedHiringProcesses = updatedHiringProcesses.map(process => {
                if (process.daysRemaining <= 1) {
                  completedHires.push({ role: process.role, count: process.count });
                  return null;
                }
                return { ...process, daysRemaining: process.daysRemaining - 1 };
              }).filter(Boolean) as any[];
            }
            
            // Add completed hires to employees
            const updatedEmployees = [...(currentState.employees || [])];
            if (completedHires.length > 0) {
              // Create new employees (simplified)
              completedHires.forEach(hire => {
                for (let j = 0; j < hire.count; j++) {
                  updatedEmployees.push({
                    id: `emp-${Date.now()}-${j}`,
                    name: `Employee ${updatedEmployees.length + 1}`,
                    role: hire.role,
                    hiredDate: newDate,
                    dailySalary: 500, // Simplified
                    performance: 75,
                    happiness: 75,
                    experience: 0
                  });
                }
              });
            }
            
            // 4. Progress product development phases
            let updatedProducts = currentState.products || [];
            if (updatedProducts.length > 0) {
              updatedProducts = updatedProducts.map(product => {
                if (!product.developmentPhase || product.status === 'production') return product;
                
                // Check if phase is complete based on days elapsed
                const daysSince = this.calculateDaysSince(product.developmentPhase.startDate, newDate);
                if (daysSince >= product.developmentPhase.estimatedDuration) {
                  // Phase complete - this should trigger phase progression
                  // For now, we'll just mark it in the console
                  console.log(`📱 Product "${product.name}" completed ${product.status} phase`);
                }
                
                return product;
              });
            }
            
            // Update state with all calculated values
            this.updateStateRef.current({ 
              day, 
              month, 
              year,
              cash: currentState.cash + dailyProfit,
              researchPoints: newResearchPoints,
              research: updatedResearch,
              employees: updatedEmployees,
              hiringProcesses: updatedHiringProcesses,
              products: updatedProducts
            });
            
            // On the first day of each month, trigger competitor actions
            if (day === 1) {
              console.log(`📅 Debug: Triggering monthly updates for ${month}/${year}`);
              
              // Calculate time elapsed for AI actions
              const currentDate = new Date(year, month - 1, day);
              const startDate = new Date(GAME_CONFIG.STARTING_YEAR, GAME_CONFIG.STARTING_MONTH - 1, GAME_CONFIG.STARTING_DAY);
              const timeElapsed = currentDate.getTime() - startDate.getTime();
              
              // Execute competitor actions (which includes AI product releases)
              await competitorAI.executeCompetitorActions(timeElapsed);
              
              // Progress AI product developments
              competitorAI.progressProductDevelopments(timeElapsed);
            }
            
            // Simulate competitor daily sales
            competitorAI.simulateCompetitorSales(newDate);
            
            // Wait a bit for React to process the state update
            await new Promise(resolve => setTimeout(resolve, 20));
          }
          
          // Resume the game if it was running before
          if (wasRunning) {
            this.simulationActionsRef.current.setPaused(false);
          }
          
          const finalState = this.gameStateRef.current;
          console.log(`✅ Advanced to ${finalState?.month}/${finalState?.day}/${finalState?.year}`);
          console.log(`💰 Cash: ${formatCurrency(finalState?.cash || 0)}`);
          console.log(`🔬 Research Points: ${finalState?.researchPoints || 0}`);
          
          // Log any AI product releases that occurred
          const totalGameDays = (finalState.year - GAME_CONFIG.STARTING_YEAR) * 360 + (finalState.month - 1) * 30 + finalState.day;
          const originalGameDays = (originalDate.year - GAME_CONFIG.STARTING_YEAR) * 360 + (originalDate.month - 1) * 30 + originalDate.day;
          console.log(`📊 Advanced ${totalGameDays - originalGameDays} game days. Check events for any AI product releases.`);
        },
        getDate: () => {
          const state = this.gameStateRef.current;
          if (!state) return { day: 1, month: 1, year: 2007 };
          return { day: state.day, month: state.month, year: state.year };
        }
      },

      company: {
        setCash: (amount: number) => {
          if (!this.updateStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          this.updateStateRef.current({ cash: amount });
          console.log(`💰 Cash set to ${formatCurrency(amount)}`);
        },
        addCash: (amount: number) => {
          if (!this.gameStateRef.current || !this.updateStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          const newCash = this.gameStateRef.current.cash + amount;
          this.updateStateRef.current({ cash: newCash });
          console.log(`💰 Added ${formatCurrency(amount)}. New balance: ${formatCurrency(newCash)}`);
        },
        setReputation: (value: number) => {
          if (!this.updateStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          const clamped = Math.max(0, Math.min(100, value));
          this.updateStateRef.current({ reputation: clamped });
          console.log(`⭐ Reputation set to ${clamped}/100`);
        },
        setMarketShare: (value: number) => {
          if (!this.updateStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          const clamped = Math.max(0, Math.min(1, value));
          this.updateStateRef.current({ marketShare: clamped });
          console.log(`📊 Market share set to ${(clamped * 100).toFixed(2)}%`);
        },
        setResearchPoints: (amount: number) => {
          if (!this.updateStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          this.updateStateRef.current({ researchPoints: amount });
          console.log(`🔬 Research points set to ${amount}`);
        },
        resetBankruptcy: () => {
          if (!this.updateStateRef.current) return;
          this.updateStateRef.current({ 
            bankruptcyWarnings: 0, 
            isGameOver: false 
          });
          console.log(`✅ Bankruptcy warnings reset`);
        },
        maxAll: () => {
          if (!this.updateStateRef.current) return;
          this.updateStateRef.current({
            cash: 999999999,
            marketShare: 1,
            reputation: 100,
            researchPoints: 99999,
            bankruptcyWarnings: 0
          });
          console.log(`🚀 All resources maxed out!`);
        },
        getStats: () => {
          const state = this.gameStateRef.current;
          if (!state) return null;

          console.table({
            Cash: formatCurrency(state.cash),
            'Market Share': `${(state.marketShare * 100).toFixed(2)}%`,
            Reputation: `${state.reputation}/100`,
            'Research Points': state.researchPoints,
            Date: `${state.month}/${state.day}/${state.year}`,
            'Bankruptcy Warnings': state.bankruptcyWarnings,
            'Game Over': state.isGameOver
          });

          return state;
        }
      },

      buildings: {
        list: async () => {
          const buildingStore = useBuildingStore.getState();
          const buildings = buildingStore.buildings;

          if (buildings.length === 0) {
            console.log('No buildings owned');
            return;
          }

          console.table(buildings.map(b => {
            const buildingType = buildingService.getBuildingType(b.buildingTypeId);
            return {
              ID: b.id,
              Type: buildingType?.name || b.buildingTypeId,
              Category: buildingType?.category || 'unknown',
              'Under Construction': b.isUnderConstruction ? '🔨 Yes' : '✅ No',
              'Office Capacity': buildingType?.capacity.officeWorkers || 0,
              'Factory Capacity': buildingType?.capacity.factoryWorkers || 0,
              'Lab Capacity': buildingType?.capacity.technicalWorkers || 0,
              Maintenance: formatCurrency(buildingType?.maintenanceCost || 0) + '/day'
            };
          }));
        },
        available: async () => {
          const currentYear = this.gameStateRef.current?.year || 2007;
          const availableBuildings = buildingService.getAvailableBuildingTypes(currentYear);
          const ownedBuildings = useBuildingStore.getState().buildings;

          console.table(availableBuildings.map(b => ({
            ID: b.id,
            Name: b.name,
            Category: b.category,
            Cost: formatCurrency(b.cost),
            'Reputation Req': b.reputationRequirement || 0,
            'Unlock Year': b.unlockYear,
            'Office Cap': b.capacity.officeWorkers,
            'Factory Cap': b.capacity.factoryWorkers,
            'Lab Cap': b.capacity.technicalWorkers,
            'Owned': ownedBuildings.some(ob => ob.buildingTypeId === b.id) ? '✅' : '❌'
          })));
        },
        buy: (buildingId: string) => {
          const buildingStore = useBuildingStore.getState();
          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2007 };

          const buildingType = buildingService.getBuildingType(buildingId);
          if (!buildingType) {
            console.error(`Building type ${buildingId} not found`);
            return;
          }

          // Create the building directly bypassing requirements
          const newBuilding = buildingService.purchaseBuilding(buildingId, currentDate);
          newBuilding.isUnderConstruction = false; // Complete instantly

          buildingStore.setBuildings([...buildingStore.buildings, newBuilding]);
          console.log(`🏢 Purchased ${buildingType.name} (bypassing requirements)`);
        },
        capacity: async () => {
          const buildingStore = useBuildingStore.getState();
          const employees = this.gameStateRef.current?.employees || [];
          const capacity = buildingService.calculateCapacity(buildingStore.buildings, employees);

          console.group('🏢 Building Capacity');
          console.log('Total Capacity:', capacity.total);
          console.log('Used:', capacity.used);
          console.log('Available:', capacity.available);
          console.log('\nBy Category:');
          console.table({
            Office: { Total: capacity.byCategory.office.total, Used: capacity.byCategory.office.used },
            Factory: { Total: capacity.byCategory.factory.total, Used: capacity.byCategory.factory.used },
            Lab: { Total: capacity.byCategory.lab.total, Used: capacity.byCategory.lab.used }
          });
          console.groupEnd();
        },
        unlock: (buildingId: string) => {
          const buildingStore = useBuildingStore.getState();
          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2007 };

          const buildingType = buildingService.getBuildingType(buildingId);
          if (!buildingType) {
            console.error(`Building type ${buildingId} not found`);
            return;
          }

          // Check if already owned
          if (buildingStore.buildings.some(b => b.buildingTypeId === buildingId)) {
            console.log(`Already own a ${buildingType.name}`);
            return;
          }

          // Create and add the building instantly
          const newBuilding = buildingService.purchaseBuilding(buildingId, currentDate);
          newBuilding.isUnderConstruction = false;

          buildingStore.setBuildings([...buildingStore.buildings, newBuilding]);
          console.log(`🔓 Unlocked ${buildingType.name} instantly`);
        },
        unlockAll: () => {
          const buildingStore = useBuildingStore.getState();
          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2007 };
          const allBuildings = buildingService.getAllBuildingTypes();
          const newBuildings: any[] = [...buildingStore.buildings];
          let addedCount = 0;

          allBuildings.forEach(buildingType => {
            // Only add if not already owned (for HQ types) or always add (for non-HQ)
            const alreadyOwned = newBuildings.some(b => b.buildingTypeId === buildingType.id);
            if (!alreadyOwned) {
              const newBuilding = buildingService.purchaseBuilding(buildingType.id, currentDate);
              newBuilding.isUnderConstruction = false;
              newBuildings.push(newBuilding);
              addedCount++;
            }
          });

          buildingStore.setBuildings(newBuildings);
          console.log(`🔓 Unlocked ${addedCount} buildings instantly`);
        },
        complete: () => {
          const buildingStore = useBuildingStore.getState();
          let completedCount = 0;

          const updatedBuildings = buildingStore.buildings.map(building => {
            if (building.isUnderConstruction) {
              completedCount++;
              return { ...building, isUnderConstruction: false };
            }
            return building;
          });

          buildingStore.setBuildings(updatedBuildings);
          console.log(`✅ Completed construction on ${completedCount} building(s)`);
        }
      },

      products: {
        list: async () => {
          try {
            // First try to get products from the game state
            if (this.productActionsRef.current?.products) {
              const gameProducts = this.productActionsRef.current.products;
              if (gameProducts.length > 0) {
                console.table(gameProducts.map(p => ({
                  ID: p.id,
                  Name: p.name,
                  Status: p.status || 'unknown',
                  Phase: p.phase || 'none',
                  Released: p.status === 'released' ? '✅' : '❌',
                  'Market Appeal': p.marketAppeal || 0,
                  Price: formatCurrency(p.price),
                  'Units Sold': p.salesMetrics?.unitsSold || 0
                })));
                return;
              }
            }
            
            // Fall back to database products
            const result = await ProductManager.getAllProducts();
            if (!result.success || !result.data || result.data.length === 0) {
              console.log('No products found');
              return;
            }

            const products = result.data;
            console.table(products.map(p => ({
              ID: p.id,
              Name: p.name,
              Status: p.metadata?.status || 'unknown',
              Phase: p.metadata?.phase || 'none',
              Released: p.status === 'released' ? '✅' : '❌',
              'Market Appeal': p.metadata?.marketAppeal || 0,
              Price: formatCurrency(p.price),
              'Units Sold': p.salesMetrics?.unitsSold || 0
            })));
          } catch (error) {
            console.error('Failed to list products:', error);
          }
        },
        skipPhase: async (productIdOrName: string) => {
          if (!this.productActionsRef.current) {
            console.error('Product actions not available');
            return;
          }

          const { products, progressProductToPhase } = this.productActionsRef.current;

          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          const product = products.find(p => p.id === foundProduct.id);
          if (!product) {
            console.error(`Product ${foundProduct.name} not found in active products`);
            return;
          }
          
          if (!product.developmentPhase) {
            console.error(`Product ${product.name} has no active development phase`);
            return;
          }
          
          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2024 };
          const currentPhase = product.developmentPhase.phase;
          
          // Determine next phase
          const phaseOrder = ['design', 'engineering', 'testing', 'manufacturing', 'production'];
          const currentIndex = phaseOrder.indexOf(currentPhase);
          const nextPhase = phaseOrder[currentIndex + 1];
          
          if (!nextPhase) {
            console.error(`Product ${product.name} is already in the final phase`);
            return;
          }
          
          // Progress to next phase
          progressProductToPhase(foundProduct.id, nextPhase as any, currentDate);
          console.log(`⏭️ Skipped ${currentPhase} phase for ${product.name}, now in ${nextPhase}`);
        },
        instantRelease: async (productIdOrName: string) => {
          if (!this.productActionsRef.current) {
            console.error('Product actions not available');
            return;
          }

          const { products, progressProductToPhase, releaseProduct } = this.productActionsRef.current;

          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          const product = products.find(p => p.id === foundProduct.id);
          if (!product) {
            console.error(`Product ${foundProduct.name} not found in active products`);
            return;
          }

          if (product.status === 'released') {
            console.log(`Product ${product.name} is already released`);
            return;
          }

          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2024 };

          // If product is not in production, advance it to production first
          if (product.status !== 'production') {
            console.log(`Advancing ${product.name} to production phase...`);
            progressProductToPhase(foundProduct.id, 'production', currentDate);
          }

          // Now release the product
          releaseProduct(foundProduct.id, currentDate);
          console.log(`🚀 Product ${product.name} instantly released!`);
        },
        setSales: async (productIdOrName: string, units: number) => {
          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          // TODO: Implement sales setting
          console.log(`📈 Set sales for ${foundProduct.name} (${foundProduct.id}) to ${units} units`);
        },
        discontinue: async (productIdOrName: string) => {
          if (!this.productActionsRef.current?.discontinueProduct) {
            console.error('Product actions not available');
            return;
          }

          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          this.productActionsRef.current.discontinueProduct(foundProduct.id);
          console.log(`🚫 Product ${foundProduct.name} (${foundProduct.id}) discontinued`);
        },
        details: async (productIdOrName: string) => {
          // Find product by ID or name
          const product = await findProductByIdOrName(productIdOrName);
          if (!product) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          console.group(`📱 Product: ${product.name}`);
          console.log('Basic Info:', {
            ID: product.id,
            Price: formatCurrency(product.price),
            'Tech Score': product.techScore,
            Created: product.createdAt
          });
          console.log('Components:', product.components);
          console.log('Metadata:', product.metadata);
          console.groupEnd();
        }
      },

      sales: {
        summary: async () => {
          const productStore = useProductStore.getState();
          const releasedProducts = productStore.products.filter(p => p.status === 'released');

          if (releasedProducts.length === 0) {
            console.log('No released products found');
            return;
          }

          console.table(releasedProducts.map(p => ({
            ID: p.id,
            Name: p.name,
            Price: formatCurrency(p.price),
            'Units Sold': p.salesMetrics?.unitsSold || 0,
            Revenue: formatCurrency(p.salesMetrics?.revenue || 0),
            'Market Share': `${((p.salesMetrics?.marketShare || 0) * 100).toFixed(2)}%`,
            'Daily Sales': p.salesMetrics?.dailySalesRate || 0
          })));

          // Total summary
          const totalRevenue = releasedProducts.reduce((sum, p) => sum + (p.salesMetrics?.revenue || 0), 0);
          const totalUnits = releasedProducts.reduce((sum, p) => sum + (p.salesMetrics?.unitsSold || 0), 0);
          console.log(`\n📊 Totals: ${totalUnits.toLocaleString()} units sold, ${formatCurrency(totalRevenue)} revenue`);
        },
        details: async (productIdOrName: string, format?: 'json' | 'csv' | 'yaml' | 'silent') => {
          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            if (format !== 'silent') {
              console.error(`Product "${productIdOrName}" not found`);
            }
            return;
          }

          const productStore = useProductStore.getState();
          const product = productStore.products.find(p => p.id === foundProduct.id);

          if (!product) {
            if (format !== 'silent') {
              console.error(`Product ${foundProduct.name} not found in store`);
            }
            return;
          }

          const profitMargin = ((product.price - product.productionCost) / product.price) * 100;

          const detailsData = {
            productId: product.id,
            productName: product.name,
            status: product.status,
            price: product.price,
            priceFormatted: formatCurrency(product.price),
            productionCost: product.productionCost,
            productionCostFormatted: formatCurrency(product.productionCost),
            profitMargin: profitMargin,
            profitMarginFormatted: `${profitMargin.toFixed(1)}%`,
            salesMetrics: product.salesMetrics || {}
          };

          if (format === 'json') {
            console.log(JSON.stringify(detailsData, null, 2));
          } else if (format === 'csv') {
            const csvRows = [
              'Field,Value',
              `Product ID,${detailsData.productId}`,
              `Product Name,${detailsData.productName}`,
              `Status,${detailsData.status}`,
              `Price,${detailsData.price}`,
              `Production Cost,${detailsData.productionCost}`,
              `Profit Margin,${detailsData.profitMargin}`,
              `Units Sold,${detailsData.salesMetrics?.unitsSold || 0}`,
              `Revenue,${detailsData.salesMetrics?.revenue || 0}`,
              `Market Share,${detailsData.salesMetrics?.marketShare || 0}`,
              `Average Rating,${detailsData.salesMetrics?.averageRating || 0}`
            ];
            console.log(csvRows.join('\n'));
          } else if (format === 'yaml') {
            console.log('productId: ' + detailsData.productId);
            console.log('productName: ' + detailsData.productName);
            console.log('status: ' + detailsData.status);
            console.log('price: ' + detailsData.price);
            console.log('productionCost: ' + detailsData.productionCost);
            console.log('profitMargin: ' + detailsData.profitMargin);
            console.log('salesMetrics:');
            console.log('  unitsSold: ' + (detailsData.salesMetrics?.unitsSold || 0));
            console.log('  revenue: ' + (detailsData.salesMetrics?.revenue || 0));
            console.log('  marketShare: ' + (detailsData.salesMetrics?.marketShare || 0));
            console.log('  averageRating: ' + (detailsData.salesMetrics?.averageRating || 0));
          } else if (format !== 'silent') {
            console.group(`📊 Sales Details: ${product.name}`);
            console.log('Status:', product.status);
            console.log('Price:', formatCurrency(product.price));
            console.log('Production Cost:', formatCurrency(product.productionCost));
            console.log('Profit Margin:', `${profitMargin.toFixed(1)}%`);
            console.log('\nSales Metrics:');
            console.table(product.salesMetrics || { message: 'No sales data' });
            console.groupEnd();
          }

          // Only return data for internal use (silent mode)
          // Otherwise returning causes console to display the object
          if (format === 'silent') {
            return detailsData;
          }
        },
        breakdown: async (productIdOrName: string, format?: 'json' | 'csv' | 'yaml') => {
          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          const productStore = useProductStore.getState();
          const product = productStore.products.find(p => p.id === foundProduct.id);

          if (!product) {
            console.error(`Product ${foundProduct.name} not found in store`);
            return;
          }

          if (product.status !== 'released') {
            console.error(`Product ${product.name} is not released yet`);
            return;
          }

          // Get sales details silently (no console output)
          const detailsData = await debugAPI.sales.details(foundProduct.id, 'silent' as any);

          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2007 };
          const companyRep = this.gameStateRef.current?.reputation || 10;
          const marketShare = this.gameStateRef.current?.marketShare || 0.01;

          // Get market segment for the product
          const marketSimulator = MarketSimulatorService.getInstance();
          const marketSize = marketSimulator.getMarketSize();
          const segment = marketSize.segments[0] || {
            id: 'mainstream',
            name: 'Mainstream',
            size: 100000000,
            priceElasticity: 1.0,
            techSensitivity: 0.8,
            brandLoyalty: 0.6,
            growthRate: 1.0,
            demographics: { ageRange: [18, 65] as [number, number], incomeRange: [20000, 100000] as [number, number], techAdoption: 'mainstream' }
          };

          const salesService = SalesCalculationService.getInstance();
          const breakdown = salesService.calculateDailySales(
            product,
            segment,
            currentDate,
            companyRep,
            marketShare
          );

          const breakdownData = {
            productDetails: detailsData,
            salesBreakdown: {
              dailySales: breakdown.dailySales,
              multipliers: {
                baseDemand: breakdown.baseDemand,
                priceMultiplier: breakdown.priceMultiplier,
                qualityMultiplier: breakdown.qualityMultiplier,
                competitionMultiplier: breakdown.competitionMultiplier,
                marketMultiplier: breakdown.marketMultiplier,
                timeMultiplier: breakdown.timeMultiplier
              },
              factors: breakdown.factors
            },
            context: {
              currentDate,
              companyReputation: companyRep,
              marketShare,
              segment: {
                id: segment.id,
                name: segment.name,
                size: segment.size
              }
            }
          };

          if (format === 'json') {
            console.log(JSON.stringify(breakdownData, null, 2));
          } else if (format === 'csv') {
            const sections = [];

            // Section 1: Product Details
            sections.push('=== Product Details ===');
            sections.push('Field,Value');
            sections.push(`Product ID,${breakdownData.productDetails?.productId || ''}`);
            sections.push(`Product Name,${breakdownData.productDetails?.productName || ''}`);
            sections.push(`Status,${breakdownData.productDetails?.status || ''}`);
            sections.push(`Price,${breakdownData.productDetails?.price || 0}`);
            sections.push(`Production Cost,${breakdownData.productDetails?.productionCost || 0}`);
            sections.push(`Profit Margin,${breakdownData.productDetails?.profitMargin || 0}`);
            sections.push('');

            // Section 2: Sales Breakdown
            sections.push('=== Sales Breakdown ===');
            sections.push('Metric,Value');
            sections.push(`Daily Sales,${breakdownData.salesBreakdown?.dailySales || 0}`);
            sections.push('');

            // Section 3: Multipliers
            sections.push('=== Multipliers ===');
            sections.push('Multiplier,Value');
            const multipliers = breakdownData.salesBreakdown?.multipliers || {};
            sections.push(`Base Demand,${multipliers.baseDemand || 0}`);
            sections.push(`Price Multiplier,${multipliers.priceMultiplier || 0}`);
            sections.push(`Quality Multiplier,${multipliers.qualityMultiplier || 0}`);
            sections.push(`Competition Multiplier,${multipliers.competitionMultiplier || 0}`);
            sections.push(`Market Multiplier,${multipliers.marketMultiplier || 0}`);
            sections.push(`Time Multiplier,${multipliers.timeMultiplier || 0}`);
            sections.push('');

            // Section 4: Context
            sections.push('=== Context ===');
            sections.push('Field,Value');
            sections.push(`Company Reputation,${breakdownData.context?.companyReputation || 0}`);
            sections.push(`Market Share,${breakdownData.context?.marketShare || 0}`);
            sections.push(`Segment,${breakdownData.context?.segment?.name || ''}`);

            console.log(sections.join('\n'));
          } else if (format === 'yaml') {
            console.log('productDetails:');
            console.log('  productId: ' + (breakdownData.productDetails?.productId || ''));
            console.log('  productName: ' + (breakdownData.productDetails?.productName || ''));
            console.log('  status: ' + (breakdownData.productDetails?.status || ''));
            console.log('  price: ' + (breakdownData.productDetails?.price || 0));
            console.log('  productionCost: ' + (breakdownData.productDetails?.productionCost || 0));
            console.log('  profitMargin: ' + (breakdownData.productDetails?.profitMargin || 0));
            console.log('salesBreakdown:');
            console.log('  dailySales: ' + (breakdownData.salesBreakdown?.dailySales || 0));
            console.log('  multipliers:');
            const multipliers = breakdownData.salesBreakdown?.multipliers || {};
            console.log('    baseDemand: ' + (multipliers.baseDemand || 0));
            console.log('    priceMultiplier: ' + (multipliers.priceMultiplier || 0));
            console.log('    qualityMultiplier: ' + (multipliers.qualityMultiplier || 0));
            console.log('    competitionMultiplier: ' + (multipliers.competitionMultiplier || 0));
            console.log('    marketMultiplier: ' + (multipliers.marketMultiplier || 0));
            console.log('    timeMultiplier: ' + (multipliers.timeMultiplier || 0));
            console.log('context:');
            console.log('  companyReputation: ' + (breakdownData.context?.companyReputation || 0));
            console.log('  marketShare: ' + (breakdownData.context?.marketShare || 0));
            console.log('  segment: ' + (breakdownData.context?.segment?.name || ''));
          } else {
            console.group(`📊 Sales Breakdown: ${product.name}`);
            console.log('\n=== Product Details ===');
            console.log('Status:', detailsData?.status);
            console.log('Price:', detailsData?.priceFormatted);
            console.log('Production Cost:', detailsData?.productionCostFormatted);
            console.log('Profit Margin:', detailsData?.profitMarginFormatted);
            console.log('\n=== Sales Breakdown ===');
            console.log('Daily Sales:', breakdown.dailySales);
            console.log('\nMultipliers:');
            console.table({
              'Base Demand': breakdown.baseDemand,
              'Price Multiplier': breakdown.priceMultiplier,
              'Quality Multiplier': breakdown.qualityMultiplier,
              'Competition Multiplier': breakdown.competitionMultiplier,
              'Market Multiplier': breakdown.marketMultiplier,
              'Time Multiplier': breakdown.timeMultiplier
            });
            console.log('\nDetailed Factors:');
            console.table(breakdown.factors);
            console.groupEnd();
          }

          // Don't return data to avoid console displaying the object
        },
        setSales: async (productIdOrName: string, units: number) => {
          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          const productStore = useProductStore.getState();
          const product = productStore.products.find(p => p.id === foundProduct.id);

          if (!product) {
            console.error(`Product ${foundProduct.name} not found in store`);
            return;
          }

          productStore.updateProduct(foundProduct.id, {
            salesMetrics: {
              ...product.salesMetrics,
              unitsSold: units,
              revenue: units * product.price
            }
          });
          console.log(`📈 Set sales for ${product.name} to ${units} units`);
        },
        history: async () => {
          const companyStore = useCompanyStore.getState();
          const finances = companyStore.finances;

          console.group('📅 Sales History');

          // Daily Stats
          console.log('\n=== Current Day ===');
          console.table({
            Revenue: formatCurrency(finances.dailyStats.revenue),
            Costs: formatCurrency(finances.dailyStats.costs),
            Profit: formatCurrency(finances.dailyStats.profit),
          });

          // Per-Product Breakdown (if available)
          if (finances.dailyStats.salesBreakdowns && finances.dailyStats.salesBreakdowns.length > 0) {
            console.log('\n=== Daily Sales by Product ===');
            console.table(finances.dailyStats.salesBreakdowns.map(sb => ({
              'Product ID': sb.productId,
              'Units Sold': sb.unitsSold,
              Revenue: formatCurrency(sb.revenue),
            })));
          }

          // Monthly Stats
          console.log('\n=== Current Month ===');
          console.table({
            Revenue: formatCurrency(finances.monthlyStats.revenue),
            Costs: formatCurrency(finances.monthlyStats.costs),
            Profit: formatCurrency(finances.monthlyStats.profit),
          });

          // Yearly Stats
          console.log('\n=== Current Year ===');
          console.table({
            Revenue: formatCurrency(finances.yearlyStats.revenue),
            Costs: formatCurrency(finances.yearlyStats.costs),
            Profit: formatCurrency(finances.yearlyStats.profit),
          });

          console.log('\n💡 Note: Historical time-series data not yet tracked. Showing current period totals only.');
          console.groupEnd();
        },
        forecast: async (productIdOrName: string) => {
          // Find product by ID or name
          const foundProduct = await findProductByIdOrName(productIdOrName);
          if (!foundProduct) {
            console.error(`Product "${productIdOrName}" not found`);
            return;
          }

          const productStore = useProductStore.getState();
          const product = productStore.products.find(p => p.id === foundProduct.id);

          if (!product) {
            console.error(`Product ${foundProduct.name} not found in store`);
            return;
          }

          if (product.status !== 'released') {
            console.error(`Product ${product.name} is not released yet. Cannot forecast.`);
            return;
          }

          const currentDate = this.gameStateRef.current || { day: 1, month: 1, year: 2007 };
          const companyRep = this.gameStateRef.current?.reputation || 10;
          const marketShare = this.gameStateRef.current?.marketShare || 0.01;

          // Get market segment for the product
          const marketSimulator = MarketSimulatorService.getInstance();
          const marketSize = marketSimulator.getMarketSize();
          const segment = marketSize.segments[0] || {
            id: 'mainstream',
            name: 'Mainstream',
            size: 100000000,
            priceElasticity: 1.0,
            techSensitivity: 0.8,
            brandLoyalty: 0.6,
            growthRate: 1.0,
            demographics: {
              ageRange: [18, 65] as [number, number],
              incomeRange: [20000, 100000] as [number, number],
              techAdoption: 'mainstream' as const
            }
          };

          const salesService = SalesCalculationService.getInstance();

          console.group(`🔮 Sales Forecast: ${product.name}`);

          // Calculate forecast for 7, 30, and 90 days
          const forecastPeriods = [
            { name: '7-Day', days: 7 },
            { name: '30-Day', days: 30 },
            { name: '90-Day', days: 90 }
          ];

          const forecasts: any[] = [];

          // Get current daily sales as baseline
          const breakdown = salesService.calculateDailySales(
            product,
            segment,
            currentDate,
            companyRep,
            marketShare
          );

          // Apply simple lifecycle decay for longer forecasts
          const dailyDecayRate = 0.998; // 0.2% daily decline

          forecastPeriods.forEach(period => {
            let totalUnits = 0;
            let totalRevenue = 0;

            for (let day = 1; day <= period.days; day++) {
              const decayMultiplier = Math.pow(dailyDecayRate, day - 1);
              const dailySales = Math.round(breakdown.dailySales * decayMultiplier);
              totalUnits += dailySales;
              totalRevenue += dailySales * product.price;
            }

            forecasts.push({
              Period: period.name,
              'Projected Units': totalUnits.toLocaleString(),
              'Projected Revenue': formatCurrency(totalRevenue),
              'Avg Daily Sales': Math.round(totalUnits / period.days).toLocaleString(),
            });
          });

          console.table(forecasts);

          console.log('\n=== Forecast Assumptions ===');
          console.log('• Current daily sales:', Math.round(breakdown.dailySales).toLocaleString());
          console.log('• Price remains constant at', formatCurrency(product.price));
          console.log('• Market conditions remain stable');
          console.log('• Assumes slight decay rate (0.2%/day) for mature products');
          console.log('• Company reputation:', companyRep);
          console.log('• Market share:', (marketShare * 100).toFixed(2) + '%');
          console.log('• Lifecycle stage:', breakdown.factors.lifecycleStage);
          console.log('\n⚠️ Note: Actual sales will vary based on market conditions, competition, and inventory availability.');

          console.groupEnd();
        }
      },

      inventory: {
        components: async () => {
          const inventoryStore = useInventoryStore.getState();
          const components = inventoryStore.components;

          if (components.size === 0) {
            console.log('No component inventory found');
            return;
          }

          const tableData: any[] = [];
          components.forEach((inv, id) => {
            tableData.push({
              ID: id,
              Name: inv.componentName,
              Category: inv.category,
              Quantity: inv.quantity,
              'Min Stock': inv.minStockLevel,
              'Max Stock': inv.maxStockLevel,
              'Unit Cost': formatCurrency(inv.unitCost),
              'Total Value': formatCurrency(inv.totalValue)
            });
          });

          console.table(tableData);
        },
        set: (componentId: string, qty: number) => {
          const inventoryStore = useInventoryStore.getState();
          inventoryStore.updateInventory(componentId, qty, 'set');
          console.log(`📦 Set ${componentId} inventory to ${qty}`);
        },
        add: (componentId: string, qty: number) => {
          const inventoryStore = useInventoryStore.getState();
          inventoryStore.updateInventory(componentId, qty, 'add');
          console.log(`📦 Added ${qty} to ${componentId} inventory`);
        },
        order: (componentId: string, qty: number) => {
          const inventoryStore = useInventoryStore.getState();
          // Create instant free order by directly adding to inventory
          inventoryStore.updateInventory(componentId, qty, 'add');
          console.log(`📦 Instant free order: Added ${qty} of ${componentId}`);
        },
        fillExistingComponents: () => {
          const inventoryStore = useInventoryStore.getState();
          const components = inventoryStore.components;
          let filledCount = 0;

          components.forEach((inv, id) => {
            if (inv.quantity < inv.maxStockLevel) {
              inventoryStore.updateInventory(id, inv.maxStockLevel, 'set');
              filledCount++;
            }
          });

          console.log(`📦 Filled ${filledCount} existing components to max stock level`);
        },
        fillExistingComponentsTo: (amount: number) => {
          const inventoryStore = useInventoryStore.getState();
          const components = inventoryStore.components;
          let filledCount = 0;

          components.forEach((_, id) => {
            inventoryStore.updateInventory(id, amount, 'set');
            filledCount++;
          });

          console.log(`📦 Set ${filledCount} existing components to ${amount} units each`);
        },
        fillAllComponents: async () => {
          if (!this.gameStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }

          const currentYear = this.gameStateRef.current.year;
          const currentMonth = this.gameStateRef.current.month;
          const availableComponents = componentReleaseService.getAllAvailableComponents(currentYear, currentMonth);

          let filledCount = 0;
          let errorCount = 0;

          for (const [categoryId, componentList] of availableComponents) {
            for (const component of componentList) {
              try {
                // Get current inventory to calculate the difference
                const inventory = await inventoryManagementService.getComponentInventoryByValue(component.value);
                const currentStock = inventory?.currentStock || 0;
                const targetStock = GAME_CONSTANTS.inventory.components.maxStockLevel;
                const quantityToAdd = targetStock - currentStock;

                if (quantityToAdd !== 0) {
                  await inventoryManagementService.updateComponentStock(
                    component.value,
                    quantityToAdd,
                    'adjustment'
                  );
                }
                filledCount++;
              } catch (error) {
                console.error(`Failed to update ${component.name}:`, error);
                errorCount++;
              }
            }
          }

          console.log(`📦 Filled ${filledCount} total components to max stock level (${GAME_CONSTANTS.inventory.components.maxStockLevel})`);
          if (errorCount > 0) {
            console.warn(`⚠️ Failed to update ${errorCount} components`);
          }
        },
        fillAllComponentsTo: async (amount: number) => {
          if (!this.gameStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }

          const currentYear = this.gameStateRef.current.year;
          const currentMonth = this.gameStateRef.current.month;
          const availableComponents = componentReleaseService.getAllAvailableComponents(currentYear, currentMonth);

          let filledCount = 0;
          let errorCount = 0;

          for (const [categoryId, componentList] of availableComponents) {
            for (const component of componentList) {
              try {
                // Get current inventory to calculate the difference
                const inventory = await inventoryManagementService.getComponentInventoryByValue(component.value);
                const currentStock = inventory?.currentStock || 0;
                const quantityToAdd = amount - currentStock;

                if (quantityToAdd !== 0) {
                  await inventoryManagementService.updateComponentStock(
                    component.value,
                    quantityToAdd,
                    'adjustment'
                  );
                }
                filledCount++;
              } catch (error) {
                console.error(`Failed to update ${component.name}:`, error);
                errorCount++;
              }
            }
          }

          console.log(`📦 Set ${filledCount} total components to ${amount} units each`);
          if (errorCount > 0) {
            console.warn(`⚠️ Failed to update ${errorCount} components`);
          }
        },
        devices: {
          list: async () => {
            const inventoryStore = useInventoryStore.getState();
            const devices = inventoryStore.devices;

            // Get all company products
            const productsResult = await ProductManager.getAllProducts();
            if (!productsResult.success || !productsResult.data) {
              console.log('No products found');
              return;
            }

            // Filter to only show player's products
            const playerProducts = productsResult.data.filter(
              product => product.metadata.companyId === 'player'
            );

            if (playerProducts.length === 0) {
              console.log('No player products found');
              return;
            }

            const tableData: any[] = [];
            playerProducts.forEach(product => {
              const inventory = devices.get(product.id);
              tableData.push({
                'Product ID': product.id,
                Name: product.name,
                'Current Stock': inventory?.currentStock || 0,
                'Target Stock': inventory?.targetStock || 0,
                'Inventory Value': inventory ? formatCurrency(inventory.inventoryValue || 0) : '$0',
                Status: product.metadata?.status || 'unknown'
              });
            });

            console.table(tableData);
          },
          add: async (productIdOrName: string, qty: number) => {
            // Find product by ID or name
            const product = await findProductByIdOrName(productIdOrName);
            if (!product) {
              console.error(`❌ Product "${productIdOrName}" not found`);
              return;
            }

            const unitValue = product.calculatedMetrics?.finalPrice || 0;

            try {
              await inventoryManagementService.updateDeviceStock(
                product.id,
                qty, // positive quantity for adding
                false, // isProduction = false
                unitValue
              );
              console.log(`📱 Added ${qty} devices to ${product.name} (${product.id}) inventory`);
            } catch (error) {
              console.error(`❌ Failed to add devices to ${product.name}:`, error);
            }
          },
          set: async (productIdOrName: string, qty: number) => {
            // Find product by ID or name
            const product = await findProductByIdOrName(productIdOrName);
            if (!product) {
              console.error(`❌ Product "${productIdOrName}" not found`);
              return;
            }

            const unitValue = product.calculatedMetrics?.finalPrice || 0;

            try {
              // Get current inventory to calculate the difference
              const currentInventory = await inventoryManagementService.getDeviceInventoryByProductId(product.id);
              const currentStock = currentInventory?.currentStock || 0;
              const quantityChange = qty - currentStock;

              if (quantityChange !== 0) {
                await inventoryManagementService.updateDeviceStock(
                  product.id,
                  quantityChange,
                  false, // isProduction = false (this is a debug adjustment)
                  unitValue
                );
              }

              console.log(`📱 Set ${product.name} (${product.id}) device inventory to ${qty}`);
            } catch (error) {
              console.error(`❌ Failed to set device inventory for ${product.name}:`, error);
            }
          }
        }
      },

      research: {
        unlockAll: () => {
          if (!this.updateStateRef.current || !this.gameStateRef.current) {
            console.error('Game state not initialized yet');
            return;
          }
          
          const allNodeIds = researchTree.branches.flatMap(b => b.nodes.map(n => n.id));
          
          // Initialize research if it doesn't exist
          const currentResearch = this.gameStateRef.current.research || {
            unlockedNodes: [],
            activeResearch: [],
            monthlyInvestment: 0,
            rpGenerationRate: 1
          };
          
          this.updateStateRef.current({
            research: {
              ...currentResearch,
              unlockedNodes: allNodeIds,
              activeResearch: [],
              monthlyInvestment: 0,
              rpGenerationRate: 1
            } as ResearchState
          });
          
          console.log(`🔓 Unlocked all ${allNodeIds.length} research nodes!`);
        },
        unlock: (nodeId: string) => {
          if (!this.updateStateRef.current || !this.gameStateRef.current?.research) return;
          
          const currentUnlocked = this.gameStateRef.current.research.unlockedNodes || [];
          if (currentUnlocked.includes(nodeId)) {
            console.log(`Node ${nodeId} is already unlocked`);
            return;
          }
          
          this.updateStateRef.current({
            research: {
              ...this.gameStateRef.current.research,
              unlockedNodes: [...currentUnlocked, nodeId]
            }
          });
          
          console.log(`🔓 Unlocked research node: ${nodeId}`);
        },
        addPoints: (amount: number) => {
          if (!this.gameStateRef.current || !this.updateStateRef.current) return;
          
          const newPoints = this.gameStateRef.current.researchPoints + amount;
          this.updateStateRef.current({ researchPoints: newPoints });
          console.log(`🔬 Added ${amount} research points. Total: ${newPoints}`);
        },
        showTree: () => {
          console.group('🌳 Research Tree');
          researchTree.branches.forEach(branch => {
            console.group(`📁 ${branch.name}`);
            branch.nodes.forEach(node => {
              console.log(`- ${node.name} (${node.id}) - Cost: ${node.cost} RP`);
            });
            console.groupEnd();
          });
          console.groupEnd();
        },
        listUnlocked: () => {
          const unlocked = this.gameStateRef.current?.research?.unlockedNodes || [];
          if (unlocked.length === 0) {
            console.log('No research nodes unlocked');
            return;
          }
          
          console.group('🔓 Unlocked Research');
          unlocked.forEach(nodeId => {
            const node = researchTree.branches
              .flatMap(b => b.nodes)
              .find(n => n.id === nodeId);
            if (node) {
              console.log(`- ${node.name} (${nodeId})`);
            }
          });
          console.groupEnd();
        },
        listActive: () => {
          const active = this.gameStateRef.current?.research?.activeResearch || [];
          if (active.length === 0) {
            console.log('No active research');
            return;
          }
          
          console.table(active.map(r => ({
            'Node ID': r.nodeId,
            Progress: `${r.progress.toFixed(1)}%`,
            Completed: r.completed ? '✅' : '❌'
          })));
        }
      },

      employees: {
        list: () => {
          const employees = this.gameStateRef.current?.employees || [];
          const hiringProcesses = this.gameStateRef.current?.hiringProcesses || [];
          
          if (employees.length === 0 && hiringProcesses.length === 0) {
            console.log('No employees or hiring processes found');
            return;
          }
          
          // Import employee service to get role counts
          import('../services/tycoon/employeeService').then(({ employeeService, EMPLOYEE_ROLES }) => {
            const stats = employeeService.getEmployeeStats(employees);
            const dailySalary = employeeService.calculateDailySalaryCosts(employees);
            
            console.group('👥 Employee Overview');
            console.log('Total Employees:', stats.total);
            console.log('\n📊 By Role:');
            Object.entries(EMPLOYEE_ROLES).forEach(([roleId, roleData]) => {
              const count = stats[roleId] || 0;
              if (count > 0) {
                console.log(`  ${roleData.name}: ${count}`);
              }
            });
            
            console.log('\n💰 Payroll:');
            console.log(`  Daily: ${formatCurrency(dailySalary)}`);
            console.log(`  Monthly: ${formatCurrency(dailySalary * 30)}`);
            console.log(`  Annual: ${formatCurrency(dailySalary * 365)}`);
            
            if (hiringProcesses.length > 0) {
              console.log('\n🚧 Hiring in Progress:');
              hiringProcesses.forEach(process => {
                const roleName = EMPLOYEE_ROLES[process.role]?.name || process.role;
                console.log(`  ${process.count} ${roleName}(s) - ${process.daysRemaining} days remaining`);
              });
            }
            
            if (employees.length > 0) {
              console.log('\n📈 Performance Metrics:');
              const avgPerformance = employees.reduce((sum, e) => sum + e.performance, 0) / employees.length;
              const avgHappiness = employees.reduce((sum, e) => sum + e.happiness, 0) / employees.length;
              const avgExperience = employees.reduce((sum, e) => sum + e.experience, 0) / employees.length;
              console.log(`  Avg Performance: ${avgPerformance.toFixed(1)}%`);
              console.log(`  Avg Happiness: ${avgHappiness.toFixed(1)}%`);
              console.log(`  Avg Experience: ${avgExperience.toFixed(1)}%`);
            }
            
            console.groupEnd();
          });
        },
        hire: (role: string, count: number = 1) => {
          if (!this.employeeActionsRef.current?.hire) {
            console.error('Employee hiring not available');
            return;
          }
          
          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES }) => {
            const resolvedRole = this.resolveEmployeeRole(role, EMPLOYEE_ROLES);
            if (!resolvedRole) {
              console.error(`Invalid role: ${role}. Valid roles: ${Object.keys(EMPLOYEE_ROLES).join(', ')}`);
              return;
            }
            
            this.employeeActionsRef.current!.hire(resolvedRole, count);
            console.log(`➕ Started hiring process for ${count} ${EMPLOYEE_ROLES[resolvedRole].name}(s)`);
          });
        },
        fire: (role: string, count: number = 1) => {
          if (!this.employeeActionsRef.current?.fire) {
            console.error('Employee firing not available');
            return;
          }
          
          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES }) => {
            const resolvedRole = this.resolveEmployeeRole(role, EMPLOYEE_ROLES);
            if (!resolvedRole) {
              console.error(`Invalid role: ${role}. Valid roles: ${Object.keys(EMPLOYEE_ROLES).join(', ')}`);
              return;
            }
            
            this.employeeActionsRef.current!.fire(resolvedRole, count);
            console.log(`➖ Fired ${count} ${EMPLOYEE_ROLES[resolvedRole].name}(s)`);
          });
        },
        setRole: (role: string, count: number) => {
          if (!this.employeeActionsRef.current) {
            console.error('Employee actions not available');
            return;
          }
          
          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES, employeeService }) => {
            const resolvedRole = this.resolveEmployeeRole(role, EMPLOYEE_ROLES);
            if (!resolvedRole) {
              console.error(`Invalid role: ${role}. Valid roles: ${Object.keys(EMPLOYEE_ROLES).join(', ')}`);
              return;
            }
            
            const currentEmployees = this.gameStateRef.current?.employees || [];
            const stats = employeeService.getEmployeeStats(currentEmployees);
            const currentCount = stats[resolvedRole] || 0;
            
            if (count > currentCount) {
              // Need to hire
              const toHire = count - currentCount;
              this.employeeActionsRef.current!.hire(resolvedRole, toHire);
              console.log(`📊 Setting ${EMPLOYEE_ROLES[resolvedRole].name} to ${count} (hiring ${toHire})`);
            } else if (count < currentCount) {
              // Need to fire
              const toFire = currentCount - count;
              this.employeeActionsRef.current!.fire(resolvedRole, toFire);
              console.log(`📊 Setting ${EMPLOYEE_ROLES[resolvedRole].name} to ${count} (firing ${toFire})`);
            } else {
              console.log(`${EMPLOYEE_ROLES[resolvedRole].name} already at ${count} employees`);
            }
          });
        },
        payroll: () => {
          const employees = this.gameStateRef.current?.employees || [];
          
          if (employees.length === 0) {
            console.log('No employees to calculate payroll');
            return;
          }
          
          import('../services/tycoon/employeeService').then(({ employeeService, EMPLOYEE_ROLES }) => {
            const stats = employeeService.getEmployeeStats(employees);
            const dailySalary = employeeService.calculateDailySalaryCosts(employees);
            
            console.group('💰 Payroll Details');
            console.log('Total Employees:', stats.total);
            console.log('\nSalary Costs:');
            console.log(`  Daily: ${formatCurrency(dailySalary)}`);
            console.log(`  Monthly: ${formatCurrency(dailySalary * 30)}`);
            console.log(`  Annual: ${formatCurrency(dailySalary * 365)}`);
            
            console.log('\nBreakdown by Role:');
            Object.entries(EMPLOYEE_ROLES).forEach(([roleId, roleData]) => {
              const count = stats[roleId] || 0;
              if (count > 0) {
                const roleDailyCost = count * roleData.baseSalary;
                console.log(`  ${roleData.name}: ${count} × ${formatCurrency(roleData.baseSalary)}/day = ${formatCurrency(roleDailyCost)}/day`);
              }
            });
            
            if (stats.total > 0) {
              console.log(`\nAverage per Employee: ${formatCurrency(dailySalary / stats.total)}/day`);
            }
            
            console.groupEnd();
          });
        },
        stats: () => {
          const employees = this.gameStateRef.current?.employees || [];
          
          if (employees.length === 0) {
            console.log('No employees found');
            return;
          }
          
          console.table(employees.map(e => ({
            Name: e.name,
            Role: e.role,
            Performance: `${e.performance}%`,
            Happiness: `${e.happiness}%`,
            Experience: `${e.experience.toFixed(1)}%`,
            'Daily Salary': formatCurrency(e.salary)
          })));
        },
        hireAll: () => {
          if (!this.employeeActionsRef.current?.hire) {
            console.error('Employee hiring not available');
            return;
          }
          
          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES }) => {
            console.log('🚀 Hiring 5 of each role...');
            Object.keys(EMPLOYEE_ROLES).forEach(role => {
              this.employeeActionsRef.current!.hire(role, 5);
            });
            console.log('✅ Started hiring process for 35 employees total');
          });
        },
        fireAll: () => {
          if (!this.employeeActionsRef.current?.setEmployees) {
            console.error('Employee actions not available');
            return;
          }

          this.employeeActionsRef.current.setEmployees([]);
          this.employeeActionsRef.current.setHiringProcesses([]);
          console.log('🔥 All employees fired and hiring processes cancelled');
        },
        add: (role: string, count: number = 1) => {
          if (!this.employeeActionsRef.current?.setEmployees) {
            console.error('Employee actions not available');
            return;
          }

          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES, employeeService }) => {
            const resolvedRole = this.resolveEmployeeRole(role, EMPLOYEE_ROLES);
            if (!resolvedRole) {
              console.error(`Invalid role: ${role}. Valid roles: ${Object.keys(EMPLOYEE_ROLES).join(', ')}`);
              return;
            }

            const currentEmployees = this.gameStateRef.current?.employees || [];
            const roleData = EMPLOYEE_ROLES[resolvedRole];
            const newEmployees = [];

            for (let i = 0; i < count; i++) {
              newEmployees.push(employeeService.createEmployee(roleData));
            }

            this.employeeActionsRef.current!.setEmployees([...currentEmployees, ...newEmployees]);
            console.log(`✨ Instantly added ${count} ${roleData.name}(s)`);
          });
        },
        addAll: () => {
          if (!this.employeeActionsRef.current?.setEmployees) {
            console.error('Employee actions not available');
            return;
          }

          import('../services/tycoon/employeeService').then(({ EMPLOYEE_ROLES, employeeService }) => {
            const currentEmployees = this.gameStateRef.current?.employees || [];
            const newEmployees: Array<ReturnType<typeof employeeService.createEmployee>> = [];

            Object.values(EMPLOYEE_ROLES).forEach(roleData => {
              for (let i = 0; i < 5; i++) {
                newEmployees.push(employeeService.createEmployee(roleData));
              }
            });

            this.employeeActionsRef.current!.setEmployees([...currentEmployees, ...newEmployees]);
            console.log(`✨ Instantly added ${newEmployees.length} employees (5 of each role)`);
          });
        }
      },
      competitors: {
        list: async () => {
          // Get competitor strategies which contain the company IDs
          const strategies = competitorAI.getCompetitorStrategies();
          const competitors: Array<{
            ID: string;
            Name: string;
            Status: string;
            Aggressiveness: string;
            Innovation: string;
            'Price Focus': string;
            'Marketing Budget': string;
            'R&D Budget': string;
          }> = [];
          
          // Map strategy data to company-like structure
          strategies.forEach((strategy, competitorId) => {
            competitors.push({
              ID: competitorId,
              Name: competitorId.charAt(0).toUpperCase() + competitorId.slice(1),
              Status: strategy.marketStatus,
              Aggressiveness: (strategy.aggressiveness * 100).toFixed(0) + '%',
              Innovation: (strategy.innovationFocus * 100).toFixed(0) + '%',
              'Price Focus': (strategy.priceCompetitiveness * 100).toFixed(0) + '%',
              'Marketing Budget': formatCurrency(strategy.marketingBudget),
              'R&D Budget': formatCurrency(strategy.rdBudget)
            });
          });
          
          if (competitors.length === 0) {
            console.log('No competitors found');
            return;
          }
          
          console.table(competitors);
        },
        inspect: async (companyId: string) => {
          const strategies = competitorAI.getCompetitorStrategies();
          const strategy = strategies.get(companyId);
          
          if (!strategy) {
            console.error(`Company ${companyId} not found`);
            return;
          }
          
          console.group(`🏢 ${companyId.charAt(0).toUpperCase() + companyId.slice(1)}`);
          console.log('Status:', strategy.marketStatus);
          console.log('Strategy:', {
            aggressiveness: (strategy.aggressiveness * 100).toFixed(0) + '%',
            innovationFocus: (strategy.innovationFocus * 100).toFixed(0) + '%',
            priceCompetitiveness: (strategy.priceCompetitiveness * 100).toFixed(0) + '%'
          });
          console.log('Budgets:', {
            marketing: formatCurrency(strategy.marketingBudget),
            rd: formatCurrency(strategy.rdBudget)
          });
          console.log('Preferred Segments:', strategy.preferredSegments);
          console.log('Reaction Speed:', strategy.reactionSpeed + ' months');
          console.groupEnd();
        },
        products: async (companyId: string) => {
          const competitorProducts = competitorAI.getCompetitorProducts();
          const products = competitorProducts.get(companyId) || [];
          
          if (products.length === 0) {
            console.log(`No products found for company ${companyId}`);
            return;
          }
          
          console.table(products.map(p => ({
            ID: p.id,
            Name: p.name,
            Price: formatCurrency(p.price),
            'Tech Score': p.techScore,
            'Market Share': (p.marketShare * 100).toFixed(2) + '%',
            'Lifecycle': p.lifecycle,
            'Launch Date': p.launchDate
          })));
        },
        metrics: async () => {
          const currentState = this.gameStateRef.current;
          if (!currentState) {
            console.log('Game state not available');
            return;
          }
          
          // Calculate total game days
          const totalGameDays = (currentState.year - GAME_CONFIG.STARTING_YEAR) * 360 + (currentState.month - 1) * 30 + currentState.day;
          const analysis = await marketShareService.calculateMarketShares(totalGameDays);
          
          // Group by company
          const companyData = new Map<string, { totalSales: number; totalRevenue: number; marketShare: number }>();
          
          analysis.marketShares.forEach(product => {
            const existing = companyData.get(product.companyId) || { totalSales: 0, totalRevenue: 0, marketShare: 0 };
            existing.totalSales += product.unitsSold;
            existing.marketShare += product.marketShare;
            companyData.set(product.companyId, existing);
          });
          
          // Convert to table format
          const tableData = Array.from(companyData.entries()).map(([companyId, data]) => ({
            Company: companyId.charAt(0).toUpperCase() + companyId.slice(1),
            'Market Share': `${(data.marketShare * 100).toFixed(2)}%`,
            'Total Sales': data.totalSales.toLocaleString()
          }));
          
          console.table(tableData);
        },
        simulateLaunch: async (companyId: string) => {
          await competitorAI.forceCompetitorProductLaunch(companyId);
          console.log(`🚀 Forced product launch for ${companyId}`);
        },
        internals: async (companyId: string) => {
          const strategies = competitorAI.getCompetitorStrategies();
          const strategy = strategies.get(companyId);
          
          if (!strategy) {
            console.error(`Company ${companyId} not found`);
            return;
          }
          
          const products = competitorAI.getCompetitorProducts().get(companyId) || [];
          const developments = competitorAI.getActiveProductDevelopments().get(companyId) || [];
          
          // console.group(`🔍 ${companyId.toUpperCase()} - Internal Stats`);
          
          console.group('💰 Financial');
          console.log('Marketing Budget:', formatCurrency(strategy.marketingBudget));
          console.log('R&D Budget:', formatCurrency(strategy.rdBudget));
          console.log('Estimated Cash:', formatCurrency(strategy.marketingBudget * 10)); // Rough estimate
          console.groupEnd();
          
          console.group('📱 Current Products');
          if (products.length === 0) {
            console.log('No active products');
          } else {
            console.table(products.map(p => ({
              Name: p.name,
              Price: formatCurrency(p.price),
              'Tech Score': p.techScore,
              'Market Share': (p.marketShare * 100).toFixed(2) + '%',
              Lifecycle: p.lifecycle,
              'Days Since Launch': p.launchDate
            })));
          }
          console.groupEnd();
          
          console.group('🚧 Products in Development');
          if (developments.length === 0) {
            console.log('No products in development');
          } else {
            console.table(developments.map(d => ({
              Phase: d.phase,
              'Start Date': d.startDate,
              'Est. Duration': d.estimatedDuration + ' days',
              Budget: formatCurrency(d.budget),
              Confidence: d.confidence + '%',
              Quality: d.qualityScore + '%'
            })));
          }
          console.groupEnd();
          
          console.group('🎯 Strategy');
          console.log('Aggressiveness:', (strategy.aggressiveness * 100).toFixed(0) + '%');
          console.log('Innovation Focus:', (strategy.innovationFocus * 100).toFixed(0) + '%');
          console.log('Price Competitiveness:', (strategy.priceCompetitiveness * 100).toFixed(0) + '%');
          console.log('Risk Tolerance:', ((strategy.riskTolerance || 0.5) * 100).toFixed(0) + '%');
          console.log('Preferred Segments:', strategy.preferredSegments.join(', '));
          console.groupEnd();
          
          console.groupEnd();
        },
        // Get all internal stats as an object (instead of console output)
        getStats: async (companyId: string) => {
          const strategies = competitorAI.getCompetitorStrategies();
          const strategy = strategies.get(companyId);
          
          if (!strategy) {
            return null;
          }
          
          const products = competitorAI.getCompetitorProducts().get(companyId) || [];
          const developments = competitorAI.getActiveProductDevelopments().get(companyId) || [];
          const marketShare = await marketShareService.getCompanyMarketShare(companyId);
          
          return {
            company: { id: companyId },
            strategy,
            products: {
              active: products.filter(p => p.lifecycle !== 'decline'),
              total: products.length
            },
            developments,
            marketMetrics: {
              currentMarketShare: marketShare
            },
            financialEstimates: {
              marketingBudget: strategy.marketingBudget,
              rdBudget: strategy.rdBudget,
              estimatedCash: strategy.marketingBudget * 10 // Rough estimate
            }
          };
        },
        // Debug all competitors at once
        inspectAll: async () => {
          const strategies = competitorAI.getCompetitorStrategies();
          console.group('🏭 All Competitors Overview');
          
          for (const [companyId, strategy] of strategies) {
            if (strategy.marketStatus === 'active') {
              console.log(`\n--- ${companyId.toUpperCase()} ---`);
              await debugAPI.competitors.internals(companyId);
            }
          }
          
          console.groupEnd();
        },
        nextReleases: () => {
          console.group('📅 Upcoming Product Releases');
          
          const currentState = this.gameStateRef.current;
          if (!currentState) {
            console.log('Game state not available');
            console.groupEnd();
            return;
          }
          
          // Import seasonal launch service
          import('./SeasonalLaunchService').then(({ seasonalLaunchService }) => {
            // Get seasonal launches for next 12 months
            const seasonalLaunches = seasonalLaunchService.getUpcomingLaunches(
              currentState.year,
              currentState.month,
              12
            );
            
            console.group('🗓️ Scheduled Seasonal Launches');
            if (seasonalLaunches.length === 0) {
              console.log('No scheduled launches in the next 12 months');
            } else {
              seasonalLaunches.slice(0, 10).forEach(launch => {
                const monthsAway = (launch.year - currentState.year) * 12 + (launch.month - currentState.month);
                const daysAway = monthsAway * 30 - (currentState.day - 1);
                console.log(`${launch.competitorId}: ${launch.productType} - ${launch.month}/${launch.year} (in ~${daysAway} days)`);
              });
              if (seasonalLaunches.length > 10) {
                console.log(`... and ${seasonalLaunches.length - 10} more`);
              }
            }
            console.groupEnd();
            
            // Get products in development
            console.group('🔨 Products in Development');
            const allDevelopments: any[] = [];
            const devMap = competitorAI.getActiveProductDevelopments();
            
            devMap.forEach((developments, companyId) => {
              developments.forEach(dev => {
                // Estimate days until release based on current phase
                const phaseDurations = { concept: 90, design: 60, development: 45, manufacturing: 30 };
                let daysUntilRelease = 0;
                const phases = ['concept', 'design', 'development', 'manufacturing'] as const;
                const currentIndex = phases.indexOf(dev.phase);
                
                for (let i = currentIndex; i < phases.length; i++) {
                  daysUntilRelease += phaseDurations[phases[i]] || 30;
                }
                
                allDevelopments.push({
                  company: companyId,
                  phase: dev.phase,
                  daysUntilRelease,
                  estimatedPrice: dev.budget / 1000, // Rough price estimate
                  confidence: dev.confidence
                });
              });
            });
            
            // Sort by estimated release date
            allDevelopments.sort((a, b) => a.daysUntilRelease - b.daysUntilRelease);
            
            if (allDevelopments.length === 0) {
              console.log('No products currently in development');
            } else {
              allDevelopments.slice(0, 5).forEach(dev => {
                console.log(`${dev.company}: ${dev.phase} phase - ~${dev.daysUntilRelease} days until release`);
              });
              if (allDevelopments.length > 5) {
                console.log(`... and ${allDevelopments.length - 5} more in development`);
              }
            }
            console.groupEnd();
            
            console.groupEnd(); // End main group
          });
        }
      },

      state: {
        export: () => {
          const state = {
            company: this.gameStateRef.current,
            products: this.productActionsRef.current?.products || [],
            timestamp: new Date().toISOString()
          };
          
          console.log('📦 Exported game state:', state);
          return state;
        },
        import: (state: { company?: Partial<CompanyState> }) => {
          if (!state?.company) {
            console.error('Invalid state format');
            return;
          }
          
          if (this.updateStateRef.current) {
            this.updateStateRef.current(state.company);
          }
          
          console.log('📥 Imported game state');
        },
        inspect: () => {
          // console.group('🔍 Current Game State');
          console.log('Company:', this.gameStateRef.current);
          console.log('Products:', this.productActionsRef.current?.products || []);
          console.groupEnd();
        },
        history: () => {
          if (this.eventHistory.length === 0) {
            console.log('No events in history');
            return;
          }
          
          console.table(this.eventHistory.slice(-20));
        },
        clear: () => {
          this.eventHistory = [];
          console.clear();
          console.log('🧹 Console and event history cleared');
        }
      },

      settings: {
        enableAutoProgression: () => {
          if (!this.updateStateRef.current || !this.gameStateRef.current) return;
          
          // Set all phase preferences to 'proceed'
          const phasePreferences = {
            design_complete: 'proceed',
            engineering_complete: 'proceed',
            testing_complete: 'proceed',
            manufacturing_complete: 'proceed'
          };
          
          // Store in localStorage for persistence
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          prefs.phasePreferences = phasePreferences;
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          // Dispatch custom event to notify UI
          window.dispatchEvent(new CustomEvent('preferenceChanged', { 
            detail: { type: 'phasePreferences', value: phasePreferences } 
          }));
          
          console.log('✅ Auto phase progression enabled - all phases set to auto-proceed');
        },
        disableAutoProgression: () => {
          // Set all phase preferences to 'ask'
          const phasePreferences = {
            design_complete: 'ask',
            engineering_complete: 'ask',
            testing_complete: 'ask',
            manufacturing_complete: 'ask'
          };
          
          // Store in localStorage for persistence
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          prefs.phasePreferences = phasePreferences;
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          // Dispatch custom event to notify UI
          window.dispatchEvent(new CustomEvent('preferenceChanged', { 
            detail: { type: 'phasePreferences', value: phasePreferences } 
          }));
          
          console.log('❌ Auto phase progression disabled - all phases set to ask');
        },
        enableWarnings: () => {
          // Enable fire warning
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          prefs.fireWarningEnabled = true;
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          console.log('✅ Termination warnings enabled');
        },
        disableWarnings: () => {
          // Disable fire warning
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          prefs.fireWarningEnabled = false;
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          console.log('❌ Termination warnings disabled');
        },
        prefillNames: () => {
          // Enable name prefilling
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          prefs.prefillCompanyName = 'Mega Inc';
          prefs.prefillCEOName = 'Mega Man';
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          console.log('✅ Name prefilling enabled (Company: Mega Inc, CEO: Mega Man)');
        },
        clearNames: () => {
          // Clear name prefilling
          const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
          const prefs = JSON.parse(savedPrefs);
          delete prefs.prefillCompanyName;
          delete prefs.prefillCEOName;
          localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
          
          console.log('❌ Name prefilling disabled');
        }
      },

      help: () => {
        console.log(getDebugHelpText());
      }
    };

      // Attach to window
      (window as any).debug = debugAPI;

      // Auto-configure dev mode settings
      this.applyAutoConfiguration();

      // Show welcome message
      console.log(`
🚀 Dev Mode Enabled!
===================
Debug functions are now available. Type debug.help() for commands.
Game speed set to max slot. Auto progression enabled.
      `);
    } catch (error) {
      console.error('Failed to enable debug mode:', error);
      this.enabled = false;
    }
  }

  disable() {
    if (!this.enabled) return;
    this.enabled = false;

    // Remove from window
    delete (window as any).debug;
    
    // Clear dev mode settings from preferences
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
    const prefs = JSON.parse(savedPrefs);
    
    // Reset phase preferences to 'ask'
    prefs.phasePreferences = {
      design_complete: 'ask',
      engineering_complete: 'ask',
      testing_complete: 'ask',
      manufacturing_complete: 'ask'
    };
    
    // Re-enable fire warnings
    prefs.fireWarningEnabled = true;
    
    // Clear name prefilling
    delete prefs.prefillCompanyName;
    delete prefs.prefillCEOName;
    
    // Re-enable music
    prefs.musicEnabled = true;
    
    localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));

    console.log('🔒 Dev Mode disabled. Debug functions removed and settings reset.');
  }

  private applyAutoConfiguration() {
    // Set game speed to the fastest configured slot
    const speedByPosition = this.resolveSpeedByPosition(
      this.simulationActionsRef.current?.availableSpeeds?.filter(speed => speed.multiplier > 0).length || 0
    );
    if (speedByPosition && this.simulationActionsRef.current?.changeSpeed) {
      this.simulationActionsRef.current.changeSpeed({
        label: speedByPosition.label,
        multiplier: speedByPosition.multiplier
      });
    }

    // Enable auto phase progression
    const phasePreferences = {
      design_complete: 'proceed',
      engineering_complete: 'proceed',
      testing_complete: 'proceed',
      manufacturing_complete: 'proceed'
    };
    
    // Disable termination warnings, enable name prefilling, and disable music
    const savedPrefs = localStorage.getItem('smartphone-simulator-preferences') || '{}';
    const prefs = JSON.parse(savedPrefs);
    prefs.phasePreferences = phasePreferences;
    prefs.fireWarningEnabled = false;
    prefs.prefillCompanyName = 'Mega Inc';
    prefs.prefillCEOName = 'Mega Man';
    prefs.musicEnabled = false;
    localStorage.setItem('smartphone-simulator-preferences', JSON.stringify(prefs));
    
    console.log('🎮 Dev mode auto-configuration applied:');
    console.log('  • Game speed: max slot');
    console.log('  • Auto phase progression: enabled');
    console.log('  • Termination warnings: disabled');
    console.log('  • Name prefilling: enabled');
    console.log('  • Music: disabled');
  }

  logEvent(event: Record<string, unknown>) {
    this.eventHistory.push({
      ...event,
      timestamp: new Date().toISOString()
    });
  }
}

export const debugService = new DebugService();
